import {Injectable, Injector} from '@angular/core';
import {Router} from '@angular/router';
import {AppAuthService} from '@app/auth';
import {Capacitor} from '@capacitor/core';
import {from, Observable, of} from 'rxjs';
import {catchError, finalize, map, shareReplay, switchMap, tap} from 'rxjs/operators';
import {MobileAuthCredentialsService} from './mobile-auth-credentials.service';
import {SessionService} from './session.service';

@Injectable({providedIn: 'root'})
export class AuthSessionRecoveryService {
	private recoveryInProgress: Observable<boolean> | null = null;
	private loggingOut = false;

	constructor(
		private injector: Injector,
		private credentialsService: MobileAuthCredentialsService,
		private router: Router,
	) {
	}

	private get authService(): AppAuthService {
		return this.injector.get(AppAuthService);
	}

	private get sessionService(): SessionService {
		return this.injector.get(SessionService);
	}

	setLoggingOut(value: boolean): void {
		this.loggingOut = value;
	}

	isLoggingOut(): boolean {
		return this.loggingOut;
	}

	saveCredentialsAfterLogin(email: string | undefined, password: string | undefined, rememberMe: boolean): Promise<void> {
		if (!Capacitor.isNativePlatform()) {
			return Promise.resolve();
		}
		if (!email || !password) {
			return Promise.resolve();
		}
		return this.credentialsService.save({email, password, rememberMe: true});
	}

	recoverSession(): Observable<boolean> {
		if (!Capacitor.isNativePlatform() || this.loggingOut) {
			return of(false);
		}
		if (this.recoveryInProgress) {
			return this.recoveryInProgress;
		}

		this.recoveryInProgress = from(this.credentialsService.getCredentials()).pipe(
			switchMap(credentials => {
				if (!credentials?.email || !credentials?.password) {
					return of(false);
				}
				return this.authService.authenticate('email', {
					email: credentials.email,
					password: credentials.password,
					rememberMe: true,
				}).pipe(
					map(result => {
						if (result.isSuccess()) {
							this.sessionService.loadPermissions();
							return true;
						}
						return false;
					}),
					catchError(() => of(false)),
				);
			}),
			finalize(() => {
				this.recoveryInProgress = null;
			}),
			shareReplay(1),
		);

		return this.recoveryInProgress;
	}

	handleSessionLost(): Observable<boolean> {
		if (this.loggingOut) {
			return of(false);
		}

		if (!Capacitor.isNativePlatform()) {
			this.redirectToLogin();
			return of(false);
		}

		return this.recoverSession().pipe(
			tap(success => {
				if (!success) {
					this.redirectToLogin();
				}
			}),
		);
	}

	private redirectToLogin(): void {
		this.authService.clearToken();
		const url = this.router.url ?? '';
		if (!url.startsWith('/auth')) {
			void this.router.navigate(['/auth/login']);
		}
	}
}
